import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classes from './profile.module.css';
import cabinetClasses from '../cabinet/cabinet.module.css';
import logo from '../../../images/logo.jpg';
import { changeWebPassword, confirmBindTelegram, getWebProfile, previewBindTelegram } from '../../../utils/api';
import {
    formatConfigDisplayId,
    getCountryIcon,
    getProtocolAndSubprotocol,
    getProtocolIconSrc,
} from '../../../utils/vpnConfigDisplay';
import TopupModal from '../../common/topup-modal/topup-modal';

const TELEGRAM_BOT_URL = 'https://t.me/EasyFastVPNBot';
const MAX_BIND_SELECT = 6;

function getTgDisplay(profile) {
    if (profile?.telegram_login) return `@${profile.telegram_login}`;
    if (profile?.telegram_name) return profile.telegram_name;
    if (profile?.telegram_id) return `id${profile.telegram_id}`;
    return null;
}

function BindConfigCardRow({ c, selected, onToggle, cabinetClasses: cab, profileClasses: pc }) {
    const pp = getProtocolAndSubprotocol(c.client);
    const flag = getCountryIcon(c.country);
    const displayId = formatConfigDisplayId(c.name);
    const protoIcon = getProtocolIconSrc(pp.protocol);
    return (
        <div className={pc.bindCardWrap}>
            <div className={pc.bindCardRow}>
                <input
                    type="checkbox"
                    className={pc.bindCheckbox}
                    checked={!!selected}
                    onChange={() => onToggle(c.id)}
                    aria-label={`Выбрать ${displayId || c.name}`}
                />
                <div className={`${cab.configCard} ${pc.bindCardGrow}`}>
                    <div className={cab.configMain2}>
                        <div className={cab.configCardRow}>
                            <div className={cab.configCardLeft}>
                                {flag ? (
                                    <img className={cab.serverCountryIcon} src={flag} alt="" />
                                ) : null}
                                <div className={cab.serverCountryColumn}>
                                    <div className={cab.serverCountry}>{c.country || '—'}</div>
                                </div>
                            </div>
                        </div>
                        <div className={cab.configMetaCompact}>
                            <div className={cab.metaItem}>ID: {displayId || '—'}</div>
                            <div className={`${cab.metaItem} ${cab.metaProtocolItem}`}>
                                {protoIcon ? (
                                    <img className={cab.protocolIconSmall} src={protoIcon} alt="" />
                                ) : null}
                                <span>
                                    {pp.protocol} {pp.subprotocol}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const Profile = () => {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [notice, setNotice] = useState(null);

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPassword2, setNewPassword2] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [bindOpen, setBindOpen] = useState(false);
    const [bindCode, setBindCode] = useState('');
    const [bindSubmitting, setBindSubmitting] = useState(false);
    const [bindStep, setBindStep] = useState('code');
    const [bindPreview, setBindPreview] = useState(null);
    const [bindSelected, setBindSelected] = useState({});
    const [passwordOpen, setPasswordOpen] = useState(false);
    const [topupOpen, setTopupOpen] = useState(false);

    useEffect(() => {
        const token = window.localStorage.getItem('efv_web_token');
        if (!token) {
            navigate('/auth');
            return;
        }

        const run = async () => {
            setLoading(true);
            try {
                const data = await getWebProfile();
                setProfile(data);
            } catch (err) {
                setNotice({ variant: 'error', text: err?.message || 'Не удалось загрузить профиль' });
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [navigate]);

    async function refreshProfileOnly() {
        try {
            const data = await getWebProfile();
            setProfile(data);
        } catch {
            /* ignore */
        }
    }

    useEffect(() => {
        if (!bindPreview || bindPreview.already_linked) return;
        const rows = [
            ...(bindPreview.telegram_configs || []),
            ...(bindPreview.web_configs || []),
        ];
        const o = {};
        rows.forEach((c, i) => {
            o[c.id] = rows.length <= MAX_BIND_SELECT ? true : i < MAX_BIND_SELECT;
        });
        setBindSelected(o);
    }, [bindPreview]);

    const toggleBindConfig = (id) => {
        setBindSelected((s) => {
            const willCheck = !s[id];
            if (willCheck) {
                const count = Object.values(s).filter(Boolean).length;
                if (count >= MAX_BIND_SELECT) {
                    setNotice({ variant: 'error', text: 'Можно выбрать не более 6 серверов' });
                    return s;
                }
            }
            return { ...s, [id]: willCheck };
        });
    };

    const displayName = profile?.telegram_name || profile?.site_login || 'Пользователь';
    const avatar = profile?.telegram_avatar || '';
    const initial = String(displayName || 'U').trim().charAt(0).toUpperCase() || 'U';
    const tgDisplay = getTgDisplay(profile);
    const tariffDay = profile?.tariff_price_per_day ?? profile?.tariff_price_day;
    const tariffMonth = profile?.tariff_price_per_month ?? profile?.tariff_price_month;
    const tariffPriceText =
        tariffDay !== null && tariffDay !== undefined && tariffMonth !== null && tariffMonth !== undefined
            ? `${tariffDay}₽/сут, ${tariffMonth}₽/мес`
            : '';

    const bindSelectedCount = Object.values(bindSelected).filter(Boolean).length;
    const proFrom = bindPreview?.pro_from_config_count ?? 4;
    const stTariff = bindPreview?.standard_tariff;
    const proTariff = bindPreview?.pro_tariff;
    const legTariff = bindPreview?.legacy_tariff;
    const legProTariff = bindPreview?.legacy_pro_tariff;
    const webTn = String(bindPreview?.web_tariff_name || '').trim();
    const legacyTrack = webTn === 'Legacy' || webTn === 'Legacy Pro';

    const bindTariffNotice = (() => {
        if (!bindPreview || bindPreview.already_linked) return null;
        const n = bindSelectedCount;
        if (n === 0) {
            return 'Серверы не выбраны — после привязки у вас не будет активных VPN-серверов.';
        }
        if (n < proFrom) {
            if (legacyTrack && legTariff) {
                const d = legTariff.price_per_day;
                const m = legTariff.price_per_month;
                if (d != null && m != null) {
                    return `При ${n} сервер${n === 1 ? 'е' : 'ах'} (до ${bindPreview.standard_tier_max ?? 3}) действует тариф: ${d}₽/сут, ${m}₽/мес.`;
                }
            }
            const d = stTariff?.price_per_day;
            const m = stTariff?.price_per_month;
            if (d != null && m != null) {
                return `При ${n} сервер${n === 1 ? 'е' : 'ах'} (до ${bindPreview.standard_tier_max ?? 3}) действует тариф: ${d}₽/сут, ${m}₽/мес.`;
            }
            return `При количестве до ${bindPreview.standard_tier_max ?? 3} серверов действует тариф Standard.`;
        }
        if (legacyTrack && legProTariff) {
            const d = legProTariff.price_per_day;
            const m = legProTariff.price_per_month;
            if (d != null && m != null) {
                return `При ${n} серверах с ${proFrom}-го включается тариф Pro: ${d}₽/сут, ${m}₽/мес.`;
            }
        }
        const d = proTariff?.price_per_day;
        const m = proTariff?.price_per_month;
        if (d != null && m != null) {
            return `При ${n} серверах с ${proFrom}-го включается тариф: ${d}₽/сут, ${m}₽/мес.`;
        }
        return `При ${n} и более серверах действует тариф Pro.`;
    })();

    const onChangePassword = async (e) => {
        e.preventDefault();
        setNotice(null);

        if (oldPassword.length < 8) {
            setNotice({ variant: 'error', text: 'Введите текущий пароль' });
            return;
        }
        if (newPassword.length < 8) {
            setNotice({ variant: 'error', text: 'Новый пароль должен быть не короче 8 символов' });
            return;
        }
        if (newPassword !== newPassword2) {
            setNotice({ variant: 'error', text: 'Новые пароли не совпадают' });
            return;
        }

        setSubmitting(true);
        try {
            await changeWebPassword({ oldPassword, newPassword });
            setOldPassword('');
            setNewPassword('');
            setNewPassword2('');
            setNotice({ variant: 'success', text: 'Пароль успешно изменён' });
        } catch (err) {
            setNotice({ variant: 'error', text: err?.message || 'Не удалось сменить пароль' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
        <div className={classes.page}>
            <div className={classes.heroMax}>
                <header className={classes.topBar}>
                    <Link to="/cabinet" className={classes.brandLink}>
                        <img className={classes.logoImg} src={logo} alt="EasyFast VPN" />
                        <span className={classes.logoAccent}>EasyFast</span>
                        <span className={classes.logoSuffix}>VPN</span>
                    </Link>
                    <div className={classes.headerNav}>
                        <Link to="/cabinet" className={classes.backBtn}>В кабинет</Link>
                        <Link to="/help" className={classes.backBtn}>Инструкция</Link>
                        <Link to="/apps" className={classes.backBtn}>Приложение</Link>
                    </div>
                </header>
            </div>

            <main className={classes.main}>
                <div className={classes.card}>
                    <h1 className={classes.cardTitle}>Профиль</h1>

                    {notice ? (
                        <div
                            className={
                                notice.variant === 'success'
                                    ? classes.noticeSuccess
                                    : notice.variant === 'info'
                                      ? classes.noticeInfo
                                      : classes.noticeError
                            }
                        >
                            {notice.text}
                        </div>
                    ) : null}

                    {loading ? (
                        <div className={classes.loading}>Загрузка...</div>
                    ) : (
                        <>
                            <div className={classes.profileTop}>
                                <div className={classes.avatarWrap}>
                                    {avatar ? (
                                        <img className={classes.avatar} src={avatar} alt="" />
                                    ) : (
                                        <div className={classes.avatarFallback}>{initial}</div>
                                    )}
                                </div>
                                <div className={classes.profileMeta}>
                                    <div className={classes.loginText}>{profile?.site_login || '—'}</div>
                                    {tgDisplay ? (
                                        <div className={classes.tgField}>Telegram: {tgDisplay}</div>
                                    ) : (
                                        <button
                                            className={classes.bindBtn}
                                            type="button"
                                            onClick={() => {
                                                setBindOpen((v) => {
                                                    const next = !v;
                                                    setBindCode('');
                                                    setBindStep('code');
                                                    setBindPreview(null);
                                                    setBindSelected({});
                                                    return next;
                                                });
                                            }}
                                        >
                                            Привязать telegram аккаунт
                                        </button>
                                    )}
                                </div>
                            </div>

                            {!tgDisplay && bindOpen ? (
                                <div className={classes.bindBox}>
                                    <p className={classes.bindHint}>
                                        Вы можете объединить web и Telegram: балансы суммируются, серверы переносятся на
                                        один аккаунт. В боте нажмите «Привязать web-аккаунт», получите код и введите его
                                        ниже.
                                        {' '}
                                        <a
                                            href={TELEGRAM_BOT_URL}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={classes.bindLink}
                                        >
                                            Открыть бота
                                        </a>
                                    </p>
                                    {bindStep === 'code' ? (
                                        <div className={classes.bindRow}>
                                            <input
                                                className={classes.input}
                                                type="text"
                                                placeholder="Код привязки"
                                                value={bindCode}
                                                onChange={(e) => setBindCode(e.target.value)}
                                                disabled={bindSubmitting}
                                            />
                                            <button
                                                type="button"
                                                className={classes.bindSubmitBtn}
                                                disabled={bindSubmitting}
                                                onClick={async () => {
                                                    setNotice(null);
                                                    const code = bindCode.trim();
                                                    if (code.length < 4) {
                                                        setNotice({ variant: 'error', text: 'Введите код из бота' });
                                                        return;
                                                    }
                                                    setBindSubmitting(true);
                                                    try {
                                                        const prev = await previewBindTelegram(code);
                                                        if (prev?.already_linked) {
                                                            setNotice({
                                                                variant: 'info',
                                                                text: 'Этот Telegram уже привязан к этому аккаунту.',
                                                            });
                                                            return;
                                                        }
                                                        setBindPreview(prev);
                                                        setBindStep('select');
                                                    } catch (err) {
                                                        setNotice({
                                                            variant: 'error',
                                                            text: err?.message || 'Не удалось проверить код',
                                                        });
                                                    } finally {
                                                        setBindSubmitting(false);
                                                    }
                                                }}
                                            >
                                                {bindSubmitting ? '…' : 'Далее'}
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className={classes.bindMaxCap}>
                                                Выбрано серверов: <strong>{bindSelectedCount}</strong> / {MAX_BIND_SELECT}
                                            </div>
                                            <div className={classes.bindSection}>
                                                <div className={classes.bindSectionTitle}>Серверы из Telegram (бот)</div>
                                                {(bindPreview?.telegram_configs || []).length === 0 ? (
                                                    <div className={classes.bindEmpty}>Нет серверов</div>
                                                ) : (
                                                    <div className={classes.bindConfigGrid}>
                                                        {(bindPreview?.telegram_configs || []).map((c) => (
                                                            <BindConfigCardRow
                                                                key={c.id}
                                                                c={c}
                                                                selected={bindSelected[c.id]}
                                                                onToggle={toggleBindConfig}
                                                                cabinetClasses={cabinetClasses}
                                                                profileClasses={classes}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className={classes.bindSection}>
                                                <div className={classes.bindSectionTitle}>Серверы с сайта</div>
                                                {(bindPreview?.web_configs || []).length === 0 ? (
                                                    <div className={classes.bindEmpty}>Нет серверов</div>
                                                ) : (
                                                    <div className={classes.bindConfigGrid}>
                                                        {(bindPreview?.web_configs || []).map((c) => (
                                                            <BindConfigCardRow
                                                                key={c.id}
                                                                c={c}
                                                                selected={bindSelected[c.id]}
                                                                onToggle={toggleBindConfig}
                                                                cabinetClasses={cabinetClasses}
                                                                profileClasses={classes}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                                    {bindTariffNotice ? (
                                                        <div className={classes.bindTariffNote}>{bindTariffNotice}</div>
                                                    ) : null}
                                                    <div className={classes.bindActions}>
                                                        <button
                                                            type="button"
                                                            className={classes.bindBackBtn}
                                                            disabled={bindSubmitting}
                                                            onClick={() => {
                                                                setBindStep('code');
                                                                setBindPreview(null);
                                                                setBindSelected({});
                                                            }}
                                                        >
                                                            Назад
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={classes.bindSubmitBtn}
                                                            disabled={bindSubmitting}
                                                            onClick={async () => {
                                                                setNotice(null);
                                                                const code = bindCode.trim();
                                                                const keepIds = Object.entries(bindSelected)
                                                                    .filter(([, v]) => v)
                                                                    .map(([id]) => Number(id));
                                                                setBindSubmitting(true);
                                                                try {
                                                                    const data = await confirmBindTelegram(
                                                                        code,
                                                                        keepIds,
                                                                    );
                                                                    setProfile(data);
                                                                    setBindCode('');
                                                                    setBindOpen(false);
                                                                    setBindStep('code');
                                                                    setBindPreview(null);
                                                                    setBindSelected({});
                                                                    setNotice({
                                                                        variant: 'success',
                                                                        text: 'Telegram успешно привязан',
                                                                    });
                                                                } catch (err) {
                                                                    setNotice({
                                                                        variant: 'error',
                                                                        text:
                                                                            err?.message ||
                                                                            'Не удалось завершить привязку',
                                                                    });
                                                                } finally {
                                                                    setBindSubmitting(false);
                                                                }
                                                            }}
                                                        >
                                                            {bindSubmitting ? '…' : 'Подтвердить привязку'}
                                                        </button>
                                                    </div>
                                        </>
                                    )}
                                </div>
                            ) : null}

                            <div className={classes.billingBox}>
                                <div className={classes.billingBox2}>
                                    <div className={classes.billingText}>Баланс: {profile?.balance ?? 0}₽</div>
                                    <div className={classes.billingSubtext}>
                                        Тариф: {tariffPriceText ? `${tariffPriceText}` : ''}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className={classes.topupBtn}
                                    onClick={() => setTopupOpen(true)}
                                >
                                    Пополнить баланс
                                </button>
                            </div>

                            <div className={classes.passwordBlock}>
                                <button
                                    type="button"
                                    className={classes.passwordToggleBtn}
                                    onClick={() => setPasswordOpen((v) => !v)}
                                >
                                    {passwordOpen ? 'Скрыть смену пароля' : 'Сменить пароль'}
                                </button>

                                {passwordOpen ? (
                                    <form className={classes.form} onSubmit={onChangePassword} noValidate>
                                        <div className={classes.sectionTitle}>Смена пароля</div>
                                        <input
                                            className={classes.input}
                                            type="password"
                                            placeholder="Текущий пароль"
                                            autoComplete="current-password"
                                            value={oldPassword}
                                            onChange={(e) => setOldPassword(e.target.value)}
                                            disabled={submitting}
                                        />
                                        <input
                                            className={classes.input}
                                            type="password"
                                            placeholder="Новый пароль"
                                            autoComplete="new-password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            disabled={submitting}
                                        />
                                        <input
                                            className={classes.input}
                                            type="password"
                                            placeholder="Повторите новый пароль"
                                            autoComplete="new-password"
                                            value={newPassword2}
                                            onChange={(e) => setNewPassword2(e.target.value)}
                                            disabled={submitting}
                                        />
                                        <button type="submit" className={classes.primaryBtn} disabled={submitting}>
                                            {submitting ? 'Сохраняем...' : 'Сменить пароль'}
                                        </button>
                                    </form>
                                ) : null}
                            </div>

                            <button
                                type="button"
                                className={classes.logoutBtn}
                                onClick={() => {
                                    window.localStorage.removeItem('efv_web_token');
                                    navigate('/auth');
                                }}
                            >
                                Выйти
                            </button>
                        </>
                    )}
                </div>
            </main>
        </div>
            <TopupModal open={topupOpen} onClose={() => setTopupOpen(false)} onProfileRefresh={refreshProfileOnly} />
        </>
    );
};

export default Profile;

